import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import useDashboardSelector from '../../../../../redux/selectorHooks/useDashboardSelector';
import { getLoadingAnalysisSelector } from '../../../../../redux/selectors/loadingAnalyses';
import DashboardReport from '../DashboardReport';
import { useLoadingQueue } from '../../../../../utils/hooks';
import {
  DndGrid,
  DndGridCell,
  DndGridRow,
  IGridPosition,
} from '../../../../../components/DndGrid';
import { ChartDragOverlay } from '../../../../../components/DndGrid/ChartDragOverlay';
import { UnreachableCaseError } from 'ts-essentials';
import RichTextEditor from '../../../../../components/RichTextEditor';
import DashboardContentActions from './DashboardContentActions';
import { DashboardMediaPopover } from '../DashboardMediaPopover/DashboardMediaPopover';
import ExternalEmbed from '../../../../../components/ExternalEmbed';
import ImageEmbed from '../../../../../components/ImageEmbed';
import { assertNotNil } from '../../../../../utils/types';
import { useDashboardEntityPermission } from '../../../../../utils/hooks/permissionHooks/useDashboardEntityPermission';
import { SingleSelectOption } from '../../../../../components/Dropdown/SingleSelect';
import useDashboardAddContent from '../../../utils/useDashboardAddContent';
import useDashboardEditContent from '../../../utils/useDashboardEditContent';
import { freeze } from '@reduxjs/toolkit';
import DelayedDimmerLoader from '../../../../../components/Loaders/DelayedDimmerLoader';
import {
  ImageEmbedUpload,
  ImageUploady,
} from '../../../../../components/ImageUploady';
import useDashboardDeleteContent from '../../../utils/useDashboardDeleteContent';

const DASHBOARD_QUERY_LIMIT = 2;

interface IProps {
  disableContentUpdates?: boolean;
  isEmbedded?: boolean;
}

const DashboardContent = ({
  disableContentUpdates,
  isEmbedded,
}: IProps = {}) => {
  const dashboard = useDashboardSelector('dashboard');
  assertNotNil(dashboard);
  const { rows } = dashboard;

  const { dashboardEntityPermissions } =
    useDashboardEntityPermission(dashboard);

  const loadingAnalysesSelector = useMemo(getLoadingAnalysisSelector, []);
  const loadingAnalyses = useSelector(loadingAnalysesSelector);

  const { shouldLoad } = useLoadingQueue(
    loadingAnalyses,
    DASHBOARD_QUERY_LIMIT,
  );

  const {
    loading: dashboardAddRunning,
    popoverProps: addPopoverProps,
    imageEmbedUploadProps: addImageEmbedUploadProps,
    createMenuItems,
  } = useDashboardAddContent(dashboard, false);

  const {
    loading: dashboardEditRunning,
    popoverProps: editPopoverProps,
    imageEmbedUploadProps: editImageEmbedUploadProps,
    canDrop,
    handleDrop,
    updateRowMinHeightHandler,
    updateColSpanHandler,
    createEditImageEmbedHandler,
    createEditExternalEmbedHandler,
    createEditRichTextHandler,
  } = useDashboardEditContent(dashboard);

  const {
    loading: dashboardDeleteRunning,
    createDeleteContentHandler,
    createDeleteReportHandler,
  } = useDashboardDeleteContent(dashboard);

  const loading =
    dashboardAddRunning || dashboardEditRunning || dashboardDeleteRunning;
  const isContentUpdatesDisabled =
    disableContentUpdates ??
    (!dashboardEntityPermissions.editDashboard || loading);

  return (
    <>
      <DelayedDimmerLoader loading={loading} />
      <DndGrid
        onDrop={handleDrop}
        canDrop={canDrop}
        disabled={isContentUpdatesDisabled}
        overlay={<ChartDragOverlay />}
        borderMenuItemsFactory={createMenuItems}
      >
        {rows.map((row, rowIndex) => {
          const { columns, minHeight, id } = row;
          return (
            <DndGridRow
              rowIndex={rowIndex}
              key={id}
              onUpdateColSpan={updateColSpanHandler}
              onUpdateRowMinHeight={updateRowMinHeightHandler}
              minHeight={minHeight}
            >
              {columns.map((content, colIndex) => {
                const gridPosition = freeze<IGridPosition>({
                  rowIndex,
                  colIndex,
                });
                if (content.__typename === 'DashboardAnalysisColumn') {
                  const { analysis, colSpan } = content;
                  const dndId = `analysis-${analysis.id}`;

                  return (
                    <DndGridCell
                      key={dndId}
                      id={dndId}
                      rowIndex={rowIndex}
                      colIndex={colIndex}
                      colSpan={colSpan}
                    >
                      <DashboardReport
                        analysisId={analysis.id}
                        entity={analysis}
                        shouldLoad={shouldLoad(analysis.id)}
                        onDelete={createDeleteReportHandler(
                          gridPosition,
                          analysis,
                        )}
                        dragId={dndId}
                        dragRowIndex={rowIndex}
                        dragColIndex={colIndex}
                        isEmbedded={isEmbedded}
                      />
                    </DndGridCell>
                  );
                } else if (content.__typename === 'DashboardRichTextColumn') {
                  const { richText, colSpan } = content;
                  const dndId = `richText-${richText.id}`;

                  const actions = [
                    <SingleSelectOption
                      key="delete"
                      iconName="trash"
                      label={'Delete from dashboard'}
                      onClick={createDeleteContentHandler(gridPosition)}
                    />,
                  ];

                  return (
                    <DndGridCell
                      key={dndId}
                      id={dndId}
                      rowIndex={rowIndex}
                      colIndex={colIndex}
                      colSpan={colSpan}
                    >
                      <DashboardContentActions
                        disabled={isContentUpdatesDisabled}
                        actions={actions}
                        dndId={dndId}
                        rowIndex={rowIndex}
                        colIndex={colIndex}
                      >
                        <RichTextEditor
                          disabled={isContentUpdatesDisabled}
                          value={richText.html}
                          onChange={createEditRichTextHandler(content.richText)}
                        />
                      </DashboardContentActions>
                    </DndGridCell>
                  );
                } else if (
                  content.__typename === 'DashboardExternalEmbedColumn'
                ) {
                  const { externalEmbed, colSpan } = content;
                  const dndId = `extEmbed-${externalEmbed.id}`;

                  const actions = [
                    <SingleSelectOption
                      key="update"
                      iconName="pencil"
                      label={'Change Media'}
                      onClick={createEditExternalEmbedHandler(
                        content.externalEmbed,
                      )}
                    />,
                    <SingleSelectOption
                      key="delete"
                      iconName="trash"
                      label={'Delete from dashboard'}
                      onClick={createDeleteContentHandler(gridPosition)}
                    />,
                  ];

                  return (
                    <DndGridCell
                      key={dndId}
                      id={dndId}
                      rowIndex={rowIndex}
                      colIndex={colIndex}
                      colSpan={colSpan}
                    >
                      <DashboardContentActions
                        disabled={isContentUpdatesDisabled}
                        actions={actions}
                        dndId={dndId}
                        rowIndex={rowIndex}
                        colIndex={colIndex}
                      >
                        <ExternalEmbed url={externalEmbed.url} />
                      </DashboardContentActions>
                    </DndGridCell>
                  );
                } else if (content.__typename === 'DashboardImageEmbedColumn') {
                  const { imageEmbed, colSpan } = content;
                  const dndId = `imageEmbed-${imageEmbed.id}`;

                  const actions = [
                    <SingleSelectOption
                      key="update"
                      iconName="pencil"
                      label={'Change Image'}
                      onClick={createEditImageEmbedHandler(content.imageEmbed)}
                    />,
                    <SingleSelectOption
                      key="delete"
                      iconName="trash"
                      label={'Delete from dashboard'}
                      onClick={createDeleteContentHandler(gridPosition)}
                    />,
                  ];

                  return (
                    <DndGridCell
                      key={dndId}
                      id={dndId}
                      rowIndex={rowIndex}
                      colIndex={colIndex}
                      colSpan={colSpan}
                    >
                      <DashboardContentActions
                        disabled={isContentUpdatesDisabled}
                        actions={actions}
                        dndId={dndId}
                        rowIndex={rowIndex}
                        colIndex={colIndex}
                      >
                        <ImageEmbed imageEmbed={imageEmbed} />
                      </DashboardContentActions>
                    </DndGridCell>
                  );
                } else {
                  throw new UnreachableCaseError(content);
                }
              })}
            </DndGridRow>
          );
        })}
      </DndGrid>
      <DashboardMediaPopover {...editPopoverProps} />
      <DashboardMediaPopover {...addPopoverProps} />
      <ImageUploady>
        <ImageEmbedUpload {...addImageEmbedUploadProps} />
      </ImageUploady>
      <ImageUploady>
        <ImageEmbedUpload {...editImageEmbedUploadProps} />
      </ImageUploady>
    </>
  );
};

export default DashboardContent;
